import React, { useEffect, useRef, useState } from 'react';
import {
  Text, FlatList, View, StyleSheet, Alert, Modal, Pressable, StatusBar,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Colors from '../constants/Colors';
import Style from '../constants/Style';
import BuildingIcon from './BuildingIcon';
import OrionArrowIcon from './24pxIcons/OrionArrowIcon';
import isMobile from './IsMobile';
import RightArrow12 from './12pxIcons/RightArrow12';
import {
  updateBuildingId, updateBuildingName, updateSlaHours,
} from '../redux/selectedBuildingSlice';
import { logEvent } from './LogEvent';
import PressableStyled from './PressableStyled';
import { UserPermission } from '../constants/Types';
import { Building } from '../screens/LocationManagement/components/AddBuildingModal';

interface BuildingSelectorProps {
  testID?: string,
}

const BuildingSelector = ({ testID }: BuildingSelectorProps) => {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const { id }: { id: string, name: string } = useSelector((state:
    { selectedBuilding: { id: string, name: string } }) => state.selectedBuilding);
  const [buildingListExpanded, setBuildingListExpanded] = useState<boolean>(false);
  const buildings: object | null | undefined = useSelector((state:
    { locations }) => state.locations.buildings);
  const buildingIds: string[] = Object.keys(buildings ?? {});
  const navigation = useNavigation();
  const selectorContainerRef = useRef<View>(null);
  const [buttonWidth, setButtonWidth] = useState<number>(0);
  const [buttonHeight, setButtonHeight] = useState<number>(0);
  const [dropdownTop, setDropdownTop] = useState<number>(0);
  const [dropdownLeft, setDropdownLeft] = useState<number>(0);
  const insets = useSafeAreaInsets();
  const topInset: number = Math.max(insets.top, StatusBar.currentHeight ?? 0, 0);
  const containerMarginTop: number = Math.max(insets.top, 32);
  const dispatch = useDispatch();
  const route = useRoute();
  const manageButtonShown: boolean = !isMobile && (isAdmin
    || permissions.includes(UserPermission.LOCATIONS_READ));

  const selectBuilding: Function = (newBuilding: Building) => {
    if (!(newBuilding && newBuilding.LocationId && newBuilding.Name)) return;
    dispatch(updateBuildingId(newBuilding.LocationId));
    dispatch(updateBuildingName(newBuilding.Name));
    dispatch(updateSlaHours((newBuilding.SlaHours)));
  };

  useEffect(() => {
    if (!buildings) return;
    selectBuilding(buildings[id] ?? buildings[buildingIds[0]]);
  }, [buildings]);

  const collapseList: () => void = () => setBuildingListExpanded(false);

  const toggleList: () => void = () => {
    logEvent('LocationManagement', 'toggle_list');

    if (buildingListExpanded) {
      setBuildingListExpanded(false);
      return;
    }

    selectorContainerRef.current?.measureInWindow((x, y) => {
      const fallbackTop = buttonHeight + containerMarginTop + 8;
      setDropdownTop(Math.max(y + buttonHeight + 8 + topInset, fallbackTop));
      setDropdownLeft(Math.max(x, 8));
      setBuildingListExpanded(true);
    });
  };

  const buildingName: () => string = () => {
    if (buildings === undefined) return 'Building list loading...';
    if (buildings === null) return 'Error loading buildings';
    if (buildingIds?.length === 0) return 'Add your first building';
    if (buildingIds?.length > 0) {
      const selectedBuilding: Building | null = (buildings && id) ? buildings[id] : null;
      if (selectedBuilding) return selectedBuilding.Name;
      return 'Choose building';
    }
    return 'Building list unavailable';
  };

  const navigateToLocationManagement: () => void = () => {
    if (isMobile) {
      Alert.alert('Location Management not available on mobile', 'Please use the desktop version');
      return;
    }
    logEvent('LocationManagement', 'manage_pressed');
    navigation.navigate('LocationManagement' as any);
  };

  const buildingListEmpty: boolean = buildingIds?.length === 0;

  return <View style={[styles.wrapper, isMobile && { marginTop: containerMarginTop }]}>
    <View
      ref={selectorContainerRef}
      collapsable={false}
      style={[
        styles.container,
        buildingListEmpty && styles.listEmptyContainer,
        (isMobile && !buildingListEmpty) && {
          borderColor: buildingListExpanded
            ? Colors.light.hereworksBlue100 : Colors.light.hereworksBlue50,
          backgroundColor: route.name === 'Menu' ? Colors.light.hereworksWhite100 : '#FFF',
        },
      ]}
    >
      <View style={{ flex: 1 }}>
        <PressableStyled
          onLayout={({ nativeEvent: { layout: { width, height } } }) => {
            setButtonWidth(width);
            setButtonHeight(height);
          }}
          disabled={!buildings || buildingIds.length === 1}
          maintainOpacityWhenDisabled
          style={styles.selector}
          onPress={buildingListEmpty ? navigateToLocationManagement : toggleList}
          testID={testID ?? 'lm-select-building-btn'}
        >
          <View style={styles.buildingNameSection}>
            <BuildingIcon color={Colors.light.hereworksLightBlue800} />
            <Text style={[styles.name, buildingListEmpty && styles.firstBuildingName]}>
              {buildingName()}
            </Text>
          </View>
          {buildingIds?.length > 1 && <OrionArrowIcon
            style={buildingListExpanded ? styles.inverted : styles.upright}
          />}
          {buildingListEmpty && <RightArrow12
            color={styles.firstBuildingName.color}
            style={styles.rightArrow}
          />}
        </PressableStyled>
      </View>
      {manageButtonShown && <View style={styles.manageButtonContainer}>
        <PressableStyled
          style={styles.manageButton}
          onPress={navigateToLocationManagement}
          testID='lm-manage-buildings-btn'
        >
          <Text style={styles.manageText}>{'Manage'}</Text>
        </PressableStyled>
      </View>}
    </View>
    <Modal
      visible={buildingListExpanded && buildingIds?.length > 1}
      transparent
      animationType='fade'
      statusBarTranslucent
      onRequestClose={collapseList}
    >
      <Pressable style={styles.dropdownBackdrop} onPress={collapseList} />
      {buildingIds?.length > 1 && <View style={[
        styles.dropdown,
        { width: buttonWidth + 2, top: dropdownTop, left: dropdownLeft },
      ]}>
        <FlatList
          data={buildingIds}
          renderItem={({ item: buildingId }) => buildingId !== id
            && <PressableStyled
              style={styles.option}
              onPress={() => {
                collapseList();
                dispatch(updateBuildingId(buildingId));
                dispatch(updateBuildingName(buildings[buildingId].Name));
                dispatch(updateSlaHours(buildings[buildingId].SlaHours));
              }}
              testID={`building-selector-list-item-${buildingId}`}
            >
              <BuildingIcon color={Colors.light.hereworksLightBlue800} />
              <Text style={styles.name}>{buildings[buildingId]?.Name}</Text>
            </PressableStyled>}
          keyExtractor={(buildingId: string) => `header_building_option_${buildingId}`}
        />
      </View>}
    </Modal>
  </View>;
};

const styles = StyleSheet.create({
  wrapper: {
    marginStart: isMobile ? 24 : 14,
    marginEnd: isMobile ? 24 : 0,
    zIndex: 2,
  },
  container: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue500,
    alignItems: 'center',
    flexDirection: 'row',
    backgroundColor: '#fff',
    height: isMobile ? undefined : 48,
    width: isMobile ? undefined : 366,
  },
  listEmptyContainer: {
    backgroundColor: Colors.light.hereworksBlue500,
  },
  selector: {
    ...Style.row,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  name: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
    marginStart: 12,
    flex: 1,
  },
  list: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: isMobile ? Colors.light.hereworksBlue100 : Colors.light.hereworksBlue500,
    backgroundColor: '#fff',
    maxHeight: isMobile ? 210 : 240,
    marginTop: 76,
    marginStart: isMobile ? 24 : undefined,
    marginEnd: isMobile ? undefined : 40,
  },
  dropdownBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: '#00000005',
  },
  dropdown: {
    position: 'absolute',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: isMobile ? Colors.light.hereworksBlue100 : Colors.light.hereworksBlue500,
    backgroundColor: '#fff',
    maxHeight: isMobile ? 210 : 240,
    elevation: 20,
    zIndex: 20,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  inverted: { transform: [{ rotate: '90deg' }] },
  upright: { transform: [{ rotate: '270deg' }] },
  manageButtonContainer: {
    borderStartColor: Colors.light.hereworksBlue100,
    borderStartWidth: 1,
  },
  manageButton: {
    paddingHorizontal: 12,
    paddingVertical: 5,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue500,
    backgroundColor: '#FFF',
    alignSelf: 'stretch',
    margin: 8,
    shadowColor: '#323247',
    shadowOpacity: 0.06,
    shadowRadius: 2,
    elevation: 2,
    shadowOffset: { width: 0, height: 2 },
  },
  manageText: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlue500,
  },
  firstBuildingName: {
    color: '#FFF',
  },
  rightArrow: {
    marginEnd: 12,
  },
  buildingNameSection: {
    ...Style.row,
    flex: 1,
  },
});

export default BuildingSelector;
